<template>
  <pdl-tabs
    v-if="tabPaneComponents.length"
    id="cms-tab-component"
    v-bind="tabsConfig.options"
    ref="cmsTabComponent"
    v-model="activeTab"
    :qaid="`cms-tab-component-${componentData.uid}`"
    :class="containerClasses"
    :style="stickyOffsetStyle"
  >
    <template #pre-nav>
      <div ref="preNav" class="flex items-center gap-4 px-2">
        <div :key="`confirming-${confirmingKey}`" class="relative">
          <pdl-form-group
            v-if="models && models.length"
            v-model="modelSelect"
            name="model-selection"
            type="select"
            :label="$t('product.family.detail.chooseModel')"
            :show-label="false"
            :multi-items="bikeModels"
            @change="scrollToContent"
          />
        </div>
        <span class="hidden md:inline-flex">
          <pdl-button
            qaid="tabs-shop-now-button"
            :inverted="!isElectraBrand"
            primary
            small
            block
            @click="scrollToBuyzone"
          >
            {{ $t('text.shopNow') }}
          </pdl-button>
        </span>
      </div>
    </template>
    <pdl-tab-pane
      v-for="tab of tabPaneComponents"
      :key="tab.uid"
      :name="tab.uid"
      :label="tab.tabLabel"
      :qaid="`cms-tab-pane-${tab.uid}`"
    >
      <cms-tab-component :component-data="tab" />
    </pdl-tab-pane>
  </pdl-tabs>
</template>
<script>
import {PdlTabs, PdlTabPane} from '@pedal/pdl-tabs';
import {PdlButton} from '@pedal/pdl-button';
import {PdlFormGroup} from '@pedal/pdl-form-group';
import CmsApi from '@/api/cms';
import {mapState, mapGetters, mapMutations, mapActions} from 'vuex';
import CmsTabComponent from '@/components/cms/CmsTabComponent.vue';
import {Url} from '@/utils/url';
import {getElementHeight, scrollToElement} from '@/utils/scroll-to';
import {Routes} from '@/constants/family-details.js';
import {useIntersectionObserver} from '@/composables/intersection-observer';

export default {
  components: {
    PdlTabs,
    PdlTabPane,
    CmsTabComponent,
    PdlFormGroup,
    PdlButton,
  },
  props: {
    componentData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const {intersectionObserver, isElementVisible: areTabsInView} = useIntersectionObserver();
    return {intersectionObserver, areTabsInView};
  },
  data() {
    return {
      confirmingKey: null,
      tabPaneComponents: [],
      preTabsNavHeight: 0,
      tabsConfig: {
        container: 'pdl-tabs',
        item: 'pdl-tab-pane',
        labelKey: 'label',
        options: {
          isSticky: true,
          isLazy: false,
          alignment: 'center',
          tabPosition: 'top',
        },
      },
    };
  },
  computed: {
    ...mapState('familyDetails', ['models', 'selectedModelId', 'buyZoneId']),
    ...mapState('backend', ['isElectraBrand']),
    ...mapState('global', ['headerHeight']),
    ...mapGetters('productDetails', ['getActiveTab']),
    ...mapGetters('storage', ['purchaseMode']),
    ...mapGetters('backend', ['occUrlParams']),

    tabComponentNames() {
      return this.componentData?.cmsTabComponents?.split(' ');
    },
    bikeModels() {
      return this.models?.map((model) => {
        return {
          value: model.code,
          displayName: model.name,
        };
      });
    },
    stickyOffsetStyle() {
      return {
        '--pre-tabs-nav-height': `${this.preTabsNavHeight}px`,
      };
    },
    modelSelect: {
      get() {
        return this.selectedModelId;
      },
      set(value) {
        if (value === this.selectedModelId) return;

        this.confirmingKey = value;
        this.confirmModelChange({
          confirmCallback: () => {
            this.$router.replace({
              name: Routes.FAMILY_DETAILS_PAGE,
              params: {
                ...this.$route.params,
                modelName: Url.encodeModelNametoUrl(this.models.find((model) => model.code === value)?.name),
                modelCode: value,
                sku: null,
              },
            });
            this.scrollToContent();
          },
          cancelCallback: () => {
            this.confirmingKey = this.selectedModelId;
          },
        });
      },
    },
    activeTab: {
      get() {
        return this.getActiveTab;
      },
      set(value) {
        this.setActiveTab(value);
        if (this.areTabsInView) {
          this.intersectionObserver.disconnect();
          this.scrollToContent();
        }
      },
    },
    containerClasses() {
      return {'is-electra': this.isElectraBrand, 'mt-2': this.purchaseMode};
    },
  },
  watch: {
    tabComponentNames: {
      async handler() {
        this.tabPaneComponents = await CmsApi.getComponentsByIds(this.occUrlParams.userId, this.tabComponentNames);
      },
      immediate: true,
    },
    tabPaneComponents: {
      handler(value) {
        if (value.length === this.tabComponentNames.length) {
          this.$nextTick(() => {
            this.setTabHeaderHeight(getElementHeight('.pdl-tabs__header'));
            this.updatePreTabsNavHeight();
            this.intersectionObserver.observe(this.$refs.cmsTabComponent.$el);
            this.activeTab = this.activeTab || this.tabPaneComponents?.[0]?.uid;
          });
        }
      },
    },
  },
  methods: {
    ...mapMutations('productDetails', ['setActiveTab', 'setTabHeaderHeight']),
    ...mapActions('familyDetails', ['confirmModelChange']),

    scrollToBuyzone() {
      scrollToElement(`#${this.buyZoneId}`, this.headerHeight);
    },
    scrollToContent() {
      scrollToElement(`#cms-tab-component`, this.headerHeight);
    },
    updatePreTabsNavHeight() {
      if (this.tabsConfig.options.isSticky) {
        this.preTabsNavHeight = this.$refs.preNav ? this.$refs.preNav.offsetHeight : 0;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pdl-tabs.is-electra {
  :deep(.pdl-tabs__header.is-sticky) {
    background-color: var(--electra-light-blue);

    .form-group {
      .select {
        background-color: theme('colors.white');

        & select {
          background-color: transparent;
          color: theme('textColor.black');
        }

        &::after {
          color: theme('textColor.black');
        }
      }
    }

    .pdl-tabs__item {
      &:hover,
      &.is-active {
        color: theme('colors.black');
      }

      color: var(--electra-gray);
    }

    .pdl-tabs__active-bar {
      background-color: theme('colors.black');
    }
  }
}

.pdl-tabs :deep() {
  .pdl-tabs__header {
    &.is-sticky {
      --tabs-sticky-offset: calc(var(--header-height) - var(--pre-tabs-nav-height, 0px) - 2rem - 1px);

      display: flex;
      flex-flow: wrap;
      align-items: center;
      justify-content: start;
      top: var(--tabs-sticky-offset);
      padding: theme('spacing.8') 0;
      row-gap: theme('spacing.6');
      column-gap: theme('spacing.8');
      background-color: theme('colors.black');
      z-index: 5;

      .pdl-tabs__nav.is-center {
        justify-content: start;

        @media (screen(xl)) {
          justify-content: center;
        }
      }

      @media (screen(xl)) {
        --tabs-sticky-offset: var(--header-height);

        flex-flow: row;
        justify-content: center;
        align-items: center;
      }

      .pdl-tabs__nav-wrap {
        width: 100%;

        @media (screen(xl)) {
          width: auto;
        }
      }

      .pdl-tabs__nav-scroll {
        padding-left: theme('spacing.4');
        padding-right: theme('spacing.4');
        padding-bottom: 1rem;
        margin-bottom: -1rem;
      }

      .form-group {
        margin-bottom: 0;

        .select {
          border-radius: var(--pdl-input-radius);
          background-color: theme('colors.black');

          & select {
            font-family: theme('fontFamily.heading');
            font-weight: theme('fontWeight.bold');
            font-size: theme('fontSize.sm');
            background-color: transparent;
            color: theme('textColor.white');
            padding: 0.8rem 3rem 0.8rem 0.8rem;
            height: auto;

            option {
              background-color: var(--gray-100);
              color: var(--white);

              &:hover {
                background-color: var(--gray-80);
              }
            }
          }

          &::after {
            color: theme('textColor.white');
          }
        }
      }

      .pdl-tabs__item {
        font-family: theme('fontFamily.heading');
        font-weight: theme('fontWeight.bold');
        font-size: theme('fontSize.xl');
        height: 2.5rem;
        line-height: 2.5rem;

        &:hover,
        &.is-active {
          color: theme('colors.white');
        }
      }

      .pdl-tabs__active-bar {
        background-color: theme('colors.white');
      }

      .pdl-tabs__nav-wrap::after {
        background-color: transparent;
      }
    }
  }

  .pdl-tabs__content {
    padding: 0;
    margin: 0;
  }
}
</style>
